/**
 * The locale registry.
 *
 * `locales.json` beside this file is the single source of truth for
 * which languages Nsibi speaks, and `nsibi/i18n_locales.py` reads the
 * SAME file rather than keeping a Python copy. Adding a language is one
 * row there plus a catalogue; nothing here changes.
 *
 * Why the JSON lives inside this package rather than at the repo root:
 * Metro's `watchFolders` stops at `clients/`, so a file above it cannot
 * be imported from the app at all. Django has no such limit and the
 * Dockerfile is `COPY . .`, so it reaches across instead.
 */
import registry from "./locales.json";

export type Direction = "ltr" | "rtl";

export type LocaleRow = {
  /** BCP-47 base tag. Region variants collapse onto these. */
  code: string;
  /** English name, for staff surfaces and logs. */
  name: string;
  /** Endonym. This is what a picker shows - a French speaker looking
   *  for their language is looking for "Français", not "French". */
  native: string;
  dir: Direction;
  /** Whether a user may CHOOSE it. A row can exist, be translated and
   *  be linted while still being unofferable - see `ar`. */
  offered: boolean;
  /** Django's own code for it, when they differ. */
  django: string;
  /** Typical expansion against English. Advisory: the real gate is the
   *  per-key budget in `_budget.json`. */
  expansion: number;
  blockedBy?: string;
};

export const LOCALES: readonly LocaleRow[] = registry.locales as LocaleRow[];

export const DEFAULT_LOCALE: string = registry.default;

/** The codes a user may actually pick, in registry order. */
export const OFFERED_LOCALES: readonly LocaleRow[] = LOCALES.filter(
  (l) => l.offered,
);

const BY_CODE = new Map(LOCALES.map((l) => [l.code, l]));

export function localeRow(code: string): LocaleRow | undefined {
  return BY_CODE.get(code);
}

export function isSupported(code: string): boolean {
  return BY_CODE.has(code);
}

/** True when a locale may be chosen, not merely when it exists. */
export function isOffered(code: string): boolean {
  return BY_CODE.get(code)?.offered === true;
}

export function directionOf(code: string): Direction {
  return BY_CODE.get(code)?.dir ?? "ltr";
}

/** The endonym, falling back to the code so an unknown tag still renders. */
export function nativeName(code: string): string {
  return BY_CODE.get(code)?.native ?? code;
}
